import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { initialPagination } from "src/Core/Common/Constants";
import { FolderColumnName, FolderType, MessageSource, SortOrder } from "src/Core/Common/Enums";
import { ApplicationState } from "src/store";
import MessageDetail from "./Common/MessageDetail";
import MessageList from "./Common/MessageList";
import { actionTypes as receivedMessageAction } from "../../types/receivedMessageTypes";
import { initialState } from "src/reducers/receivedMessageReducers";
import { fetchMessages, moveToFolder, printMessages } from "src/actionCreators/receivedMessageActionCreators";
import { initialReceivedMessage, IReceivedMessageModel } from "src/Core/ViewModels/Message/ReceivedMessageModel";
import { initialMessage, ISentMessageModel } from "src/Core/ViewModels/Message/SentMessageModel";
import { IFileModel } from "src/Core/ViewModels/File/FileModel";
import { fetchSubFolders, fetchUserOwnedFolders } from "src/actionCreators/folderActionCreators";
import { initialFolder } from "src/Core/ViewModels/Folder/FolderModel";
import { logger } from "src/routes/LoggedIn";
import { appInsightsData } from "src/helper/AppInsightsData";
import { currentPageChanged, inboxPageChanged } from "src/actionCreators/signalRActionCreators";
import { ResourceIdLocators } from "src/helper/ResourceIdLocators";
import { getMailboxUserEmailId } from "src/helper/HelperFunctions";
import { fetchRetentionSettings } from "src/actionCreators/adminSettingsActionCreators";
import "./styles.scss";
import {
    getThreadedMessageDetail,
    handleChangeExpiryThreadMessages,
    handleDeleteThreadMessages,
    handleThreadsDownloadAllAttachments,
    handleThreadsDownloadSingleAttachment,
} from "src/helper/MessageHelperThreads";

const Inbox: React.FC = () => {
    const [isMessageDetailShow, toggleMessageDetailShow] = useState(false);
    const [pagination, setPagination] = useState(initialPagination);
    const [resetSelection, setSelection] = useState<boolean>(false);
    const dispatch = useDispatch();
    const messageState = useSelector((state: ApplicationState) => state.receivedMessageState);
    const { exchangeEnabled } = useSelector((state: ApplicationState) => state.postAuthState);
    const mailboxUserEmailId = getMailboxUserEmailId();
    const [reload, setReload] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState<ISentMessageModel | IReceivedMessageModel>(initialMessage);
    const sentMessageState = useSelector((state: ApplicationState) => state.sentMessageState);
    // For Inbox page always show quick reply
    const showQuickReply = true;

    useEffect(() => {
        // Page View Track call
        logger.trackPageView(appInsightsData.MailBox.Inbox.PageTitle);
        dispatch(currentPageChanged(FolderType.INBOX));
        dispatch(fetchUserOwnedFolders());
        dispatch(fetchRetentionSettings());

        return () => {
            dispatch({
                type: receivedMessageAction.RESET_MESSAGES_ACTION,
                payload: initialState,
            });
        };
    }, []);

    useEffect(() => {
        exchangeEnabled &&
            dispatch(fetchSubFolders(initialFolder.folderId, FolderColumnName.FOLDERNAME, SortOrder.ASC));
    }, [exchangeEnabled, mailboxUserEmailId]);

    const fetchInboxMessages = (
        pageNo: number,
        pageSize: number,
        sortColumn?: string,
        sortOrder?: string,
        search?: string
    ) => {
        exchangeEnabled && dispatch(fetchMessages(FolderType.INBOX, pageNo, pageSize, sortColumn, sortOrder, search));
    };

    const pageNumberChanged = (pageNo: number) => {
        dispatch(inboxPageChanged(pageNo));
    };

    const messageDetailCallback = (result: any) => {
        toggleMessageDetailShow(result.messageDetail ? true : false);
    };

    const handleGetMessageDetail = (message: IReceivedMessageModel | ISentMessageModel) => {
        getThreadedMessageDetail(dispatch, message, messageDetailCallback, FolderType.INBOX);
    };

    const handleMessageDetailView = (result: boolean) => {
        if (result) toggleMessageDetailShow(false);
    };

    const handleOnChangeExpiry = (
        expiresDate: Date,
        message: ISentMessageModel | IReceivedMessageModel,
        callback?: (data: boolean) => void
    ): void => {
        handleChangeExpiryThreadMessages(dispatch, expiresDate, message, FolderType.INBOX, callback);
    };

    const handleOnDeleteMessage = (messages: IReceivedMessageModel[], callback: (data: boolean) => void) => {
        handleDeleteThreadMessages(dispatch, messages, callback, FolderType.INBOX);
    };

    const handleDownloadSingleAttachment = (
        fileId: number,
        physicalPath: string,
        messageId: number,
        fileName: string,
        senderEmail: string,
        receivedMessageGuid: string,
        messageSource: MessageSource,
        folderId: number
    ) => {
        handleThreadsDownloadSingleAttachment(
            dispatch,
            fileId,
            physicalPath,
            messageId,
            fileName,
            senderEmail,
            receivedMessageGuid,
            messageSource,
            folderId,
            (result: any) => {
                if (result) {
                    window.open(result, "_blank");
                }
            }
        );
    };

    const handleDownloadAllAttachments = (
        files: IFileModel[],
        messageId: number,
        senderEmail: string,
        receivedMessageGuid: string,
        messageSource: MessageSource,
        folderId: number
    ) => {
        handleThreadsDownloadAllAttachments(
            dispatch,
            files,
            messageId,
            senderEmail,
            receivedMessageGuid,
            messageSource,
            folderId,
            (result: any) => {
                if (result) {
                    window.open(result, "_blank");
                }
            }
        );
    };

    const handleOnMoveToFolder = (messages: IReceivedMessageModel[], folderId: number, callback?: any): void => {
        dispatch(
            moveToFolder(
                messages,
                FolderType.INBOX,
                folderId,
                callback,
                appInsightsData.MailBox.Inbox.PageTitle,
                ResourceIdLocators.Inbox.MsgListBtnMoveMessage
            )
        );
    };

    const handleOnPrint = (): void => {
        dispatch(printMessages());
    };

    const checkFolderExpiry = (folderId: number): boolean => {
        //Need to confirm folder expiry logic implementation
        if (folderId) {
            return true;
        }
        return true;
    };

    const handleResetSearchAndSort = () => {
        setReload(value => !value);
    };

    return (
        <div className="message-list-wrapper search-box-no-border-radius inbox-page-container">
            <MessageList
                showMessageDetail={() => toggleMessageDetailShow(true)}
                folderId={FolderType.INBOX}
                folderType={FolderType.INBOX}
                isLoading={messageState.isLoading}
                messages={messageState.data.messages}
                totalRecords={messageState.data.totalRecords}
                fetchMessages={fetchInboxMessages}
                pageNumberChanged={pageNumberChanged}
                deleteMessages={handleOnDeleteMessage}
                getMessageDetail={handleGetMessageDetail}
                print={handleOnPrint}
                checkFolderExpiry={checkFolderExpiry}
                moveToFolder={handleOnMoveToFolder}
                hideMessageDetailView={handleMessageDetailView}
                pagination={pagination}
                setPagination={setPagination}
                resourceLocators={ResourceIdLocators.Inbox}
                clearCheckBoxSelection={resetSelection}
                setSelection={setSelection}
                reload={reload}
                selectedMessage={selectedMessage}
                setSelectedMessage={setSelectedMessage}
            />

            {isMessageDetailShow && (
                <MessageDetail
                    message={
                        selectedMessage.folderId === FolderType.SENT
                            ? sentMessageState.mailData.data
                            : messageState.mailData.data
                    }
                    downloadSingleAttachment={handleDownloadSingleAttachment}
                    downloadAllAttachments={handleDownloadAllAttachments}
                    messageId={selectedMessage.receivedMessageId || initialReceivedMessage.receivedMessageId}
                    folderType={selectedMessage.folderId === FolderType.SENT ? FolderType.SENT : FolderType.INBOX}
                    hideMessageDetailView={handleMessageDetailView}
                    getMessageDetail={handleGetMessageDetail}
                    changeExpiry={handleOnChangeExpiry}
                    pagination={pagination}
                    fetchMessages={fetchInboxMessages}
                    hideMessageDetail={handleMessageDetailView}
                    resourceLocators={
                        selectedMessage.folderId === FolderType.SENT
                            ? ResourceIdLocators.Sent
                            : ResourceIdLocators.Inbox
                    }
                    resetSelection={setSelection}
                    resetSearchAndSort={handleResetSearchAndSort}
                    showQuickReply={showQuickReply}
                />
            )}
        </div>
    );
};

export default Inbox;
